/*
 *  -----------------------------------------------------------------------
 *    <copyright file="add-member-dialog/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';
import React, { useEffect, useState } from 'react';

import { useTranslation } from '../../../../hooks';
import Button from '../../core/buttons';

import './styles.scss';
import CharactersRemaining from '../../core/characters-remaining';
import { GET_RESTRICTED_STUDY, SAVE_SM_STUDY_MEMBER, SAVE_STUDY_MEMBER } from '../../../../queries/studies';
import { CustomDialog, DialogActions, DialogTitle } from '../../core/dialog';
import TextField from '../../core/text-field';

interface AddMemberDialogInterface {
    open: boolean;
    close(action: string): any;
    study: any;
    setCurrentEmail(email: string): any;
    allMembers: any[];
    isSmMember?: boolean;
}

const AddMemberDialog = ({ open, close, study, setCurrentEmail, allMembers, isSmMember }: AddMemberDialogInterface) => {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [screenView, setScreenView] = useState('main-screen');
    const [saveStudyMember] = useMutation(isSmMember ? SAVE_SM_STUDY_MEMBER : SAVE_STUDY_MEMBER);

    const checkExistingUser = (emailForCheck: string) =>
        study?.organisation?.members.find((member: any) =>
            member.user
                ? member.user?.email.toLowerCase() === emailForCheck.toLowerCase()
                : member.email?.toLowerCase() === emailForCheck?.toLowerCase(),
        );
    let existingUser = checkExistingUser(email);

    const checkEmail = (emailForCheck: string) =>
        allMembers.findIndex((member: any) =>
            member.user?.email
                ? member.user.email.toLowerCase() === emailForCheck.toLowerCase()
                : member.email?.toLowerCase() === emailForCheck.toLowerCase(),
        ) >= 0;

    const onClose = (action?: string) => {
        setScreenView('main-screen');
        setEmail('');
        close(action);
    };

    const validation = (checkRequired: boolean) => {
        let isValid = true;
        let emailError = '';
        const emailReg =
            /^[\W]*([\w+\-.%]+@[\w\-.]+\.[A-Za-z]{2,4}[\W]*;{1}[\W]*)*([\w+\-.%]+@[\w\-.]+\.[A-Za-z]{2,4})[\W]*$/;

        if (!email && checkRequired) {
            isValid = false;
            emailError = t('uam:EmailIsRequired');
        }

        if (email && checkRequired && !emailReg.test(email.toLowerCase())) {
            isValid = false;
            emailError = t('uam:incorrectEmailFormat');
        }

        if (email.length > 320) {
            isValid = false;
            emailError = `${t('limitExceededBy')} ${Math.abs(email.length - 320)} ${t('characters')}`;
        }

        setError(emailError);

        return isValid;
    };

    useEffect(() => {
        validation(false);
    }, [email]);

    const onChange = (event: any) => {
        setEmail(event.target.value);
    };

    const submit = (event: any) => {
        event.preventDefault();
        if (validation(true)) {
            if (checkEmail(email)) {
                setError(t('uam:StudyMemberAlreadyExists'));
            } else {
                existingUser = checkExistingUser(email);

                if (existingUser) {
                    if (!existingUser.user) setScreenView('email-found-not-registered-yet');
                    else if (existingUser.isActive) setScreenView('email-found');
                    else setScreenView('email-inactive');
                } else setScreenView('email-not-found');
            }
        }
    };

    const addNewMember = () => {
        setCurrentEmail(email);
        onClose('add-member');
    };

    const addExistingUserToTeam = (likeAdmin?: boolean) => {
        const studyMemberKey = isSmMember ? 'smStudyMember' : 'studyMember';
        saveStudyMember({
            variables: {
                studyId: study.id,
                [studyMemberKey]: {
                    id: null,
                    firstName: existingUser.firstName,
                    lastName: existingUser.lastName,
                    active: existingUser.isActive,
                    isAdmin: likeAdmin,
                    userId: existingUser.userId,
                    email: email || null,
                },
            },
            refetchQueries: [
                {
                    query: GET_RESTRICTED_STUDY,
                    variables: { id: study.id },
                },
            ],
        })
            .then(() => {
                onClose('assigned');
            })
            .catch(() => {
                onClose('error');
            });
    };

    const mainView = () => (
        <form onSubmit={submit}>
            <TextField
                label={`${t('email')}*`}
                id="add-member-email"
                fullWidth
                value={email}
                onChange={onChange}
                placeholder={t('uam:enterAnEmailAddress')}
                helperText={
                    <CharactersRemaining id="add-member-dialog-email" length={email.length} max={320} error={error} />
                }
                variant="outlined"
                color="secondary"
                error={!!error}
                className="before-select"
            />
            <DialogActions className="add-member-dialog-content-actions">
                <Button className="add-member-btn" text="add" onClick={submit} />
            </DialogActions>
        </form>
    );

    const emailFoundView = () => (
        <>
            <p className="email-found-top-text">{t('uam:isAnOPRAUser', { email })}</p>
            <p className="email-found-bottom-text">{t('uam:wouldYouLikeToAddThemToTheStudyTeam')}</p>
            <DialogActions className="add-member-dialog-content-actions-email-found">
                <Button
                    className="add-member-btn"
                    text="uam:addStudyTeamAdministrator"
                    onClick={() => {
                        addExistingUserToTeam(true);
                    }}
                />
                <Button
                    className="add-member-btn"
                    text="uam:addTeamMember"
                    onClick={() => {
                        addExistingUserToTeam(false);
                    }}
                />
            </DialogActions>
        </>
    );
    const emailNotFoundView = () => (
        <>
            <p className="email-found-top-text">{t('uam:doesNotHaveAnOPRAAccount', { email })}</p>
            <p className="email-found-bottom-text">
                {t('uam:wouldYouLikeToAddThemToTheStudyTeamToReceiveNotifications')}
            </p>
            <DialogActions className="add-member-dialog-content-actions-email-found">
                <Button
                    className="add-member-btn"
                    text="uam:enterEmailAddress"
                    onClick={() => {
                        setEmail('');
                        setScreenView('main-screen');
                    }}
                />
                <Button className="add-member-btn" text="uam:addTeamMember" onClick={addNewMember} />
            </DialogActions>
        </>
    );

    const inactiveView = () => (
        <>
            <p className="email-found-top-text">
                {t('uam:isAnInactiveUserAndDoesNOTHaveAccessToTheSystem', { email })}
            </p>
            <p className="email-found-bottom-text">{t('uam:wouldYouLikeToEnterDifferentEmailAddress')}</p>
            <DialogActions className="add-member-dialog-content-actions">
                <Button
                    className="add-member-btn"
                    text="uam:enterEmailAddress"
                    onClick={() => {
                        setEmail('');
                        setScreenView('main-screen');
                    }}
                />
            </DialogActions>
        </>
    );

    const notRegisteredYet = () => (
        <>
            <p className="email-found-top-text">{t('uam:notCompletedAccountRegistration', { email })}</p>
            <p className="email-found-bottom-text">{t('uam:wouldYouLikeToEnterDifferentEmailAddress')}</p>
            <DialogActions className="add-member-dialog-content-actions">
                <Button
                    className="add-member-btn"
                    text="uam:enterEmailAddress"
                    onClick={() => {
                        setEmail('');
                        setScreenView('main-screen');
                    }}
                />
            </DialogActions>
        </>
    );

    const chooseView = (viewName: string) => {
        switch (viewName) {
            case 'email-found-not-registered-yet':
                return notRegisteredYet();
            case 'email-found':
                return emailFoundView();
            case 'email-not-found':
                return emailNotFoundView();
            case 'email-inactive':
                return inactiveView();
            default:
                return mainView();
        }
    };

    const chooseTitleView = (viewName: string) => {
        switch (viewName) {
            case 'email-found-not-registered-yet':
                return t('uam:userDidntCompleteAccountRegistration');
            case 'email-found':
                return t('uam:emailFound');
            case 'email-not-found':
                return t('uam:emailNotFound');
            case 'email-inactive':
                return t('uam:inactiveUser');
            default:
                return t('uam:addStudyMember');
        }
    };

    return (
        <CustomDialog
            onClose={() => {
                onClose();
            }}
            fullWidth
            maxWidth="md"
            scroll="paper"
            open={open}
            className="custom-width-popup"
        >
            <DialogTitle id="add-member-modal-title">
                {chooseTitleView(screenView)}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => {
                        onClose();
                    }}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className="add-member-dialog-content">{chooseView(screenView)}</DialogContent>
        </CustomDialog>
    );
};

export default AddMemberDialog;
